import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { useLayoutEffect, useRef, useState } from 'react';

import { ScrollArea } from '@nucleus-commerce/ui/components/scroll-area';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { type TemporalLocale } from './locale';
import { scrollWithin } from './scroll';
import {
  addMonthsToMonth,
  isMonthInRange,
  MAX_YEAR,
  MIN_YEAR,
  monthKey,
  type CalendarDate,
  type CalendarMonth,
} from './temporal';

/** Years listed either side of the shown one when no min or max narrows the list. */
export const YEAR_SPAN = 100;

export interface MonthYearChooserProps {
  locale: TemporalLocale;
  /** The month the calendar shows; its year opens expanded and its month is marked. */
  display: CalendarMonth;
  today: CalendarDate;
  min: CalendarDate | null;
  max: CalendarDate | null;
  onPick: (month: CalendarMonth) => void;
}

/** The years the chooser lists: the shown year ± `YEAR_SPAN`, narrowed by min and max. */
export function chooserYears(
  display: CalendarMonth,
  min: CalendarDate | null,
  max: CalendarDate | null,
): number[] {
  const from = Math.max(MIN_YEAR, display.year - YEAR_SPAN, min?.year ?? MIN_YEAR);
  const to = Math.min(MAX_YEAR, display.year + YEAR_SPAN, max?.year ?? MAX_YEAR);
  return Array.from({ length: Math.max(0, to - from + 1) }, (_, i) => from + i);
}

/**
 * Chrome's month/year chooser: a scrolling list of years with one year open as a 3 × 4 grid of
 * months. It opens on the shown year, scrolled to the top of the list; arrows walk the months
 * (Up/Down a row, PageUp/PageDown a year, Home/End the ends of the year) and open the next year
 * when they cross into it; Enter or a click picks the month and returns to the days.
 */
export function MonthYearChooser({
  locale,
  display,
  today,
  min,
  max,
  onPick,
}: MonthYearChooserProps) {
  const [years] = useState(() => chooserYears(display, min, max));
  const [active, setActive] = useState<CalendarMonth>(display);
  const [expanded, setExpanded] = useState(display.year);
  const rootRef = useRef<HTMLDivElement>(null);
  const pending = useRef<'start' | 'nearest' | null>('start');

  const viewport = () =>
    rootRef.current?.querySelector<HTMLElement>('[data-slot="scroll-area-viewport"]');
  const yearRow = (year: number) =>
    rootRef.current?.querySelector<HTMLElement>(`[data-year="${year}"]`);
  const activeButton = () =>
    rootRef.current?.querySelector<HTMLButtonElement>('[data-month-option][tabindex="0"]');

  // Open on the expanded year at the top and focus its marked month; later, keep focus in view.
  useLayoutEffect(() => {
    const align = pending.current;
    if (!align) return;
    pending.current = null;
    if (align === 'start') scrollWithin(viewport(), yearRow(expanded), 'start');
    const button = activeButton();
    scrollWithin(viewport(), button, 'nearest');
    button?.focus({ preventScroll: true });
  }, [active, expanded]);

  const first = years[0] ?? display.year;
  const last = years.at(-1) ?? display.year;
  const inList = (month: CalendarMonth) =>
    month.year >= first && month.year <= last && isMonthInRange(month, min, max);

  const moveTo = (target: CalendarMonth) => {
    let next = target;
    // Step back toward the start until the month is listed and in range.
    const direction = monthKey(target) >= monthKey(active) ? -1 : 1;
    let guard = 0;
    while (!inList(next) && guard < 24) {
      next = addMonthsToMonth(next, direction);
      guard += 1;
    }
    if (!inList(next)) return;
    pending.current = 'nearest';
    setActive(next);
    setExpanded(next.year);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    const moves: Record<string, () => CalendarMonth> = {
      ArrowLeft: () => addMonthsToMonth(active, -1),
      ArrowRight: () => addMonthsToMonth(active, 1),
      ArrowUp: () => addMonthsToMonth(active, -4),
      ArrowDown: () => addMonthsToMonth(active, 4),
      PageUp: () => addMonthsToMonth(active, -12),
      PageDown: () => addMonthsToMonth(active, 12),
      Home: () => ({ year: active.year, month: 1 }),
      End: () => ({ year: active.year, month: 12 }),
    };
    const move = moves[event.key];
    if (!move) return;
    event.preventDefault();
    moveTo(move());
  };

  return (
    <div ref={rootRef} data-slot="month-year-chooser">
      <ScrollArea className="h-[14.25rem]">
        <div className="flex flex-col gap-px pr-2.5">
          {years.map((year) => {
            const open = year === expanded;
            return (
              <div key={year} data-year={year}>
                <button
                  type="button"
                  tabIndex={-1}
                  aria-expanded={open}
                  className={cn(
                    'hover:bg-accent hover:text-accent-foreground flex h-8 w-full items-center justify-between rounded-md px-2 text-sm tabular-nums outline-none',
                    open && 'font-medium',
                  )}
                  onClick={() => {
                    if (open) return;
                    // A year opened by the pointer scrolls to the top, as in Chrome.
                    pending.current = 'start';
                    setExpanded(year);
                    const month = { year, month: active.month };
                    if (inList(month)) {
                      setActive(month);
                    } else {
                      const firstInRange = Array.from({ length: 12 }, (_, i) => ({
                        year,
                        month: i + 1,
                      })).find(inList);
                      if (firstInRange) setActive(firstInRange);
                    }
                  }}
                >
                  {year}
                  {open ? (
                    <ChevronUpIcon aria-hidden className="text-muted-foreground size-4" />
                  ) : (
                    <ChevronDownIcon aria-hidden className="text-muted-foreground size-4" />
                  )}
                </button>
                {open ? (
                  <div
                    role="group"
                    aria-label={String(year)}
                    className="grid grid-cols-4 gap-1 px-1 pt-1 pb-2"
                  >
                    {locale.monthShortNames.map((name, index) => {
                      const month = { year, month: index + 1 };
                      const isActive = active.year === year && active.month === index + 1;
                      const isShown = display.year === year && display.month === index + 1;
                      const isToday = today.year === year && today.month === index + 1;
                      return (
                        <button
                          key={name}
                          type="button"
                          data-month-option=""
                          tabIndex={isActive ? 0 : -1}
                          disabled={!inList(month)}
                          aria-label={locale.formatMonthYear(year, index + 1)}
                          aria-current={isToday ? 'date' : undefined}
                          data-selected={isShown ? '' : undefined}
                          className={cn(
                            'flex h-8 items-center justify-center rounded-md border border-transparent text-sm transition-colors outline-none',
                            'hover:bg-accent hover:text-accent-foreground',
                            'focus-visible:ring-ring/50 focus-visible:ring-[3px]',
                            'disabled:pointer-events-none disabled:opacity-40',
                            isToday && 'border-primary',
                            isShown &&
                              'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground',
                          )}
                          onClick={() => onPick(month)}
                          onKeyDown={onKeyDown}
                        >
                          {name}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
}
